import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { getSurveyContentForSession, getSurveyQuestionForSession } from '@/common/services';
import { SurveyFeedbackStatus } from '@/constants';
import {
  capitalizeFirstLetter,
  formatDate,
  formatTimeRange,
  BookingSessionViewModel,
} from '@/common/utils';
import { useAuth } from '@/hooks';
import { CenteredSpinner } from '../centered-spinner';
import { ModalContent, ModalDialog, ModalHeader } from '../modal';
import { ConfirmedIcon } from '../icons';
import styles from './styles.module.scss';
import { FeedbackEditableView } from './editable-view';
import { FeedbackReadonlyView } from './readonly-view';

type Props = {
  session: BookingSessionViewModel;
  hideMarkAsReadBtn?: boolean;
  onCancel: VoidFunction;
};

export function FeedbackModal(props: Props) {
  const { isAdvisor, selectedRole } = useAuth();
  const { session, hideMarkAsReadBtn, onCancel } = props;
  const { t } = useTranslation();
  const questionsQuery = useQuery({
    queryKey: ['surveyQuestions', session.id, selectedRole],
    queryFn: () => getSurveyQuestionForSession(session.id, selectedRole!),
    cacheTime: 0, // no caching
    staleTime: 0, // always considered stale
  });

  const contentQuery = useQuery({
    queryKey: ['surveyContent', session.id],
    queryFn: () => getSurveyContentForSession(session.id),
    cacheTime: 0,
    staleTime: 0,
  });

  const isLoading = questionsQuery.isLoading || contentQuery.isLoading;
  const questions = contentQuery.data?.survey?.questions || questionsQuery.data?.questions || [];
  const surveyId = contentQuery.data?.surveyId || '';
  const answers = contentQuery.data?.surveyAnswers || [];
  const isFeedbackCompleted = session.feedbackStatus === SurveyFeedbackStatus.Completed;

  return (
    <ModalDialog width="70%">
      <ModalHeader onCancel={onCancel}>
        <div className={styles.infoRow}>
          <div className={styles.iconWrapper}>
            <ConfirmedIcon size="large" />
          </div>
          <div className={styles.textWrapper}>
            <div className={classNames(styles.mainText)}>
              {isAdvisor
                ? t('myFeedbacks.advisorFeedbackDetailsTitle')
                : t('myFeedbacks.userFeedbackDetailsTitle')}
            </div>
            <div className={styles.subText}>
              {t('home.inboxBlock.bookingId', { bookingId: session.id })}
            </div>
          </div>
        </div>
        <div className={styles.titleRow}>
          <div className={styles.block}>
            <div className={styles.label}>{t('home.date')}:</div>
            <div>{capitalizeFirstLetter(formatDate(session.startTime, 'dddd DD MMM'))}</div>
          </div>
          <div className={styles.block}>
            <div className={styles.label}>{t('home.time')}:</div>
            <div>{formatTimeRange(session.startTime, session.endTime)}</div>
          </div>
          {isAdvisor && (
            <div className={styles.block}>
              <div className={styles.label}>{t('home.customer')}:</div>
              <div>
                {session.customerFullName}
                {session.customerCompanyName ? `, ${session.customerCompanyName}` : ''}
              </div>
            </div>
          )}
          {!isAdvisor && (
            <div className={styles.block}>
              <div className={styles.label}>{t('home.advisor')}:</div>
              <div>{session.advisorName}</div>
            </div>
          )}

          <div className={styles.block}>
            <div className={styles.label}>{t('home.purpose')}:</div>
            <div>{session.purpose}</div>
          </div>

          <div className={styles.block}>
            <div className={styles.label}>{t('home.problem')}:</div>
            <div>{session.description}</div>
          </div>
        </div>
      </ModalHeader>
      <ModalContent>
        {isLoading && <CenteredSpinner />}

        {!isLoading && !isFeedbackCompleted && (
          <FeedbackEditableView
            sessionId={session.id}
            feedbackStatus={session.feedbackStatus}
            surveyId={surveyId}
            questions={questions}
            answers={answers}
            onCancel={onCancel}
          />
        )}

        {!isLoading && isFeedbackCompleted && (
          <FeedbackReadonlyView
            questions={questions}
            answers={answers}
            sessionId={session.id}
            onCancel={onCancel}
            hideMarkAsReadBtn={hideMarkAsReadBtn!!}
          />
        )}
      </ModalContent>
    </ModalDialog>
  );
}
